/**
 * PATH: packages/email-hub/src/feature/hooks/useEmailBackfill.ts
 * FILE_ID: EMAILHUB-FEATURE-HOOKS-USEEMAILBACKFILL-001
 * PURPOSE: Canonical body of this email hooks module. Lifted
 *          from apps/<tenant>/src/features/email/hooks/useEmailBackfill.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/hooks/useEmailBackfill.ts: thin re-export
 *     shim that points back here.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { newRequestId, reportClientIssue } from '../../_internal/services/integrationEvents'
import type { SyncResult } from './useEmails'
import { fetchEmailSyncStatus, setSyncQueuedWaiting } from './useEmails'
import { resolveQueuedChunk } from './useEmailBackfill.resolve'

// Window steps in days. Backend `EmailSyncInputSerializer` v3 accepts
// 1..3650; each chunk is fired with `force_full=true` to bypass the
// (currently no-op) delta token and pull a fresh date-range slice.
//
// The intermediate steps (30/90/180/365) cover the common case fast,
// then we keep widening (730 = 2y, 1825 = 5y, 3650 = 10y) so that a
// mailbox with deeper retention than expected will still be fully
// pulled in. Each successive empty step at the largest window costs
// us one Graph round-trip every 5 minutes, so the long-tail walk is
// effectively free.
const STEPS = [30, 90, 180, 365, 730, 1825, 3650] as const
const MAX_DAYS = STEPS[STEPS.length - 1]

export const BACKFILL_PROGRESS_MS = 180_000       // 3 min when finding new mail
export const BACKFILL_COOLDOWN_MS = 10 * 60_000   // 10 min when steady-state
/**
 * First-connect aggressive cadence. While ``total_chunks`` is still
 * tiny (the user just connected Outlook and has barely any history
 * pulled), use this much shorter wait between chunks instead of
 * BACKFILL_PROGRESS_MS. This is the difference between "I connected
 * Outlook 2 minutes ago and only see 18 emails" and "I connected
 * Outlook 2 minutes ago and see 500 emails arriving" — same Celery
 * worker, same Graph API, just a tighter walker cadence on the
 * critical first few minutes (jake 2026-05-04).
 *
 * 45s gives the Celery worker time to actually finish the previous
 * chunk before we queue the next one (typical Graph pull is 5–15s
 * for a 30-day window) without leaving the user staring at a frozen
 * count. Widened from 20s (with PROGRESS 90s->180s, COOLDOWN 5->10min)
 * because the old cadence, stacked with the mount auto-sync and the
 * polling loops, drove load_per_cpu past the backend capacity guard
 * (1.5) and surfaced the "System is busy (CPU …)" toast on inbox open.
 */
export const BACKFILL_BOOTSTRAP_MS = 45_000      // 45s during first ~5 chunks
/**
 * Threshold for "we're still bootstrapping". Once total_chunks crosses
 * this we drop back to the BACKFILL_PROGRESS_MS / COOLDOWN cadence.
 * 5 chunks at 20s ≈ 100s of aggressive ingest right after connect,
 * which is the window where user attention is highest.
 */
export const BACKFILL_BOOTSTRAP_CHUNK_LIMIT = 5
/**
 * Completion threshold: number of consecutive empty chunks at the
 * largest window before we mark the backfill complete. 3 (rather
 * than 2) keeps us robust against a single transient Graph blip
 * that returns 0 — a stuck delta cursor or a brief 5xx — without
 * stranding the walker in "complete" while there's still mail to
 * pull.
 */
export const COMPLETE_AFTER_EMPTY = 3

export type BackfillOutcome = 'pending' | 'progress' | 'complete' | 'error'

export interface BackfillState {
  oldest_days: number
  consecutive_empty: number
  last_run_at: number
  last_outcome: BackfillOutcome
  last_message: string
  complete: boolean
  /** Total chunks attempted across all runs (lifetime metric for UI). */
  total_chunks: number
  /** Sum of `emails_created` across every chunk this state has seen. */
  total_created: number
  /** Schema version — bumped whenever the state-machine semantics
   *  change so we can drop stale localStorage entries safely. */
  schema_version: number
}

/** Bump this whenever you change the state-machine logic in a way that
 *  would render older states inconsistent. Loaders below treat any
 *  state with a different version as fresh-start.
 *
 *  v3: STEPS widened from [7,14,21,30] to [30,90,180,365] now that
 *      the backend accepts up to 365. Old persisted states had cursors
 *      capped at 30 with `complete=true`, which would freeze the new
 *      walker at the old horizon — drop them so v3 starts fresh.
 *  v4: STEPS widened to [30,90,180,365,730,1825,3650] (≈10 years)
 *      and `COMPLETE_AFTER_EMPTY` raised from 2 to 3. Old v3 states
 *      that declared "complete at 365 days" should be invalidated so
 *      the walker can extend the walk into the new wider windows. */
const SCHEMA_VERSION = 4

const DEFAULT_STATE: BackfillState = {
  oldest_days: 0,
  consecutive_empty: 0,
  last_run_at: 0,
  last_outcome: 'pending',
  last_message: 'Ready',
  complete: false,
  total_chunks: 0,
  total_created: 0,
  schema_version: SCHEMA_VERSION,
}

export function backfillStorageKey(
  orgId: number | null | undefined,
  userId: number | null | undefined,
): string {
  return `email_backfill_state_${orgId ?? '0'}_${userId ?? '0'}`
}

function loadState(key: string): BackfillState {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return { ...DEFAULT_STATE }
    const parsed = JSON.parse(raw) as Partial<BackfillState>
    // Drop states from older state-machine versions — their cursor
    // semantics may not match what the current loop expects (e.g. v1
    // could deadlock at the smallest window with consecutive_empty
    // climbing without bound; that's invalid under v2).
    if (parsed.schema_version !== SCHEMA_VERSION) {
      return { ...DEFAULT_STATE }
    }
    // Belt-and-braces: even within the current version, an impossible
    // shape (lots of empties at a tiny cursor) means something went
    // wrong. Reset rather than persist the badness.
    if (
      typeof parsed.consecutive_empty === 'number' &&
      typeof parsed.oldest_days === 'number' &&
      parsed.consecutive_empty > 4 &&
      parsed.oldest_days < 90 &&
      parsed.complete !== true
    ) {
      return { ...DEFAULT_STATE }
    }
    return { ...DEFAULT_STATE, ...parsed }
  } catch {
    return { ...DEFAULT_STATE }
  }
}

function saveState(key: string, state: BackfillState): void {
  try {
    localStorage.setItem(key, JSON.stringify(state))
  } catch {
    // Storage full or disabled — fail silently. State will be ephemeral
    // for this tab; the loop still works, it just won't survive reloads.
  }
}

/** Compute progress 0-100 across the configured horizon. */
export function backfillProgressPct(state: BackfillState): number {
  return state.complete
    ? 100
    : Math.min(99, Math.round((state.oldest_days / MAX_DAYS) * 100))
}

export const BACKFILL_MAX_DAYS = MAX_DAYS
export const BACKFILL_STEPS = STEPS
/** Re-exported so UI surfaces (Settings page status pills) can render
 *  the empty-streak progress without hardcoding the threshold. */
export const BACKFILL_COMPLETE_AFTER_EMPTY = COMPLETE_AFTER_EMPTY

/** Pick the next ``days_back`` window strictly larger than the current. */
function nextStep(oldest: number): number {
  const next = STEPS.find(d => d > oldest)
  if (next === undefined) return MAX_DAYS
  return next
}

/**
 * Pure decision (I->O, no side effects — unit-tested in
 * useEmailBackfill.complete.test.ts): has the progressive backfill
 * exhausted the mailbox's supported history?
 *
 * ROOT-CAUSE GUARD (recurrence of BUG-2026-06-10 `found==synced` churn):
 * the ``created`` argument is the backend ``emails_synced`` figure, which
 * is ``emails_created + emails_updated`` (see
 * organisation/services/email_sync_service/service.py). The
 * /api/emails/sync/status/ row exposes NO created-only count, so a re-pull
 * of the already-ingested widest window reports every row as *updated* ->
 * ``created`` stays > 0 forever and the old
 * ``consecutive_empty >= COMPLETE_AFTER_EMPTY`` check NEVER fired. Result:
 * the walker re-pulled the entire 10-year window every ~90s (backend
 * worker/DB churn re-upserting the same N emails, a fresh
 * EMAIL_SYNC_QUEUED_EVENT each pass that re-triggered inbox/search
 * refetches, plus console spam) and the backfill never terminated.
 *
 * Fix: the widest window (MAX_DAYS) is a SUPERSET of every narrower step,
 * so ONE clean pass at the horizon means there is nothing older to
 * discover — the walk is done.
 *   - clean horizon pass that ingested anything (created > 0) -> complete
 *     immediately (this is exactly the re-update churn we must stop on);
 *   - clean horizon pass that found nothing (created === 0) -> fall back
 *     to the empty-streak guard so a single transient Graph 0 does not
 *     strand the walk prematurely;
 *   - errored / timed-out horizon pass -> never complete; retry next tick
 *     (the cursor stays pinned at MAX_DAYS via ``nextStep``).
 *
 * New mail at the recent end is unaffected: it arrives via the
 * independent auto-sync (last-7-days) loop, not the historical walker.
 */
export function isBackfillComplete(args: {
  reachedHorizon: boolean
  created: number
  consecutiveEmpty: number
  queuedErrored: boolean
  queuedTimedOut: boolean
}): boolean {
  const { reachedHorizon, created, consecutiveEmpty, queuedErrored, queuedTimedOut } = args
  if (!reachedHorizon) return false
  if (queuedErrored || queuedTimedOut) return false
  if (created > 0) return true
  return consecutiveEmpty >= COMPLETE_AFTER_EMPTY
}

interface UseEmailBackfillArgs {
  organisationId: number | null | undefined
  userId: number | null | undefined
  /** Whether sync prerequisites are met (Outlook connected + not expired). */
  enabled: boolean
  /** Reference to the actual sync function so we don't recreate the loop. */
  sync: (daysBack: number, opts?: { forceFull?: boolean }) => Promise<SyncResult>
  /** Reference to a "is a sync currently in flight?" flag (manual or auto). */
  isSyncing: boolean
  /** Notify caller a chunk just completed (so they can update lastSyncAt). */
  onChunkCompleted?: (result: SyncResult, daysBack: number) => void
}

export interface UseEmailBackfillReturn {
  state: BackfillState
  /** Progress percentage 0-100 across the configured horizon. */
  progressPct: number
  /** True when the loop attempted a step in the last few seconds. */
  running: boolean
  /** Force the loop to start over from day 0. */
  reset: () => void
  /** Manually trigger the next chunk now (bypasses cadence guard). */
  step: () => Promise<void>
}

export function useEmailBackfill({
  organisationId,
  userId,
  enabled,
  sync,
  isSyncing,
  onChunkCompleted,
}: UseEmailBackfillArgs): UseEmailBackfillReturn {
  const key = backfillStorageKey(organisationId, userId)
  const [state, setState] = useState<BackfillState>(() => loadState(key))
  const [running, setRunning] = useState(false)

  // Refs let the timer-driven loop read the latest state without
  // re-installing the interval on every render.
  const stateRef = useRef(state)
  stateRef.current = state
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled
  const isSyncingRef = useRef(isSyncing)
  isSyncingRef.current = isSyncing

  // Re-load whenever the key changes (different user / org).
  useEffect(() => {
    setState(loadState(key))
  }, [key])

  // Cross-tab + cross-component sync: any other instance (settings
  // page, second open tab, etc.) that writes the same localStorage
  // entry will trigger this listener so the visible state stays
  // consistent everywhere.
  useEffect(() => {
    const onStorage = (ev: StorageEvent) => {
      if (ev.key !== key) return
      setState(loadState(key))
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [key])

  const persist = useCallback((next: BackfillState) => {
    saveState(key, next)
    setState(next)
    // Notify same-tab listeners (read-only views) — the browser only
    // emits the native `storage` event in OTHER tabs, never the writer.
    try {
      window.dispatchEvent(new StorageEvent('storage', { key, newValue: JSON.stringify(next) }))
    } catch {
      // ignore — event constructor unsupported
    }
  }, [key])

  const reset = useCallback(() => {
    persist({ ...DEFAULT_STATE, last_message: 'Reset - will re-walk history' })
  }, [persist])

  const step = useCallback(async (): Promise<void> => {
    const current = stateRef.current
    if (current.complete) return
    if (isSyncingRef.current) return
    if (!enabledRef.current) return

    // Pre-flight: if the backend already has a `running` sync log for
    // this org, DON'T fire another POST — every concurrent dispatch
    // gets 429'd by EmailSyncView's in-progress guard, which the
    // walker would then mis-interpret as a network error and advance
    // anyway. Wait for the next tick instead. (The previous version
    // happily stacked 41 dispatches against a wedged worker, which
    // is exactly the failure mode this MR is fixing.)
    const preflight = await fetchEmailSyncStatus()
    if (preflight?.active_sync) {
      const ageS = preflight.active_sync.age_seconds ?? 0
      const next: BackfillState = {
        ...current,
        last_run_at: Date.now(),
        last_outcome: 'progress',
        last_message: `Worker still on previous chunk (running ${Math.round(ageS)}s) - backing off.`,
      }
      persist(next)
      try {
        // eslint-disable-next-line no-console
        console.debug('[backfill] active sync detected; skipping dispatch', {
          age_seconds: ageS, sync_log_id: preflight.active_sync.id,
        })
      } catch { /* ignore */ }
      return
    }

    setRunning(true)
    const requestId = newRequestId('backfill')
    const days = nextStep(current.oldest_days)
    // Server-time anchor for the queued-poll filter. We stamp
    // *before* the POST so the BE's `?since=` filter accepts the
    // EmailSyncLog row this chunk is about to create.
    const firedAt = new Date().toISOString()
    const startedAt = Date.now()

    try {
      // force_full=true: each backfill chunk asks the backend to do
      // an absolute date-range fetch (since=now-days_back) rather
      // than a delta-cursor fetch. Without this, once auto-sync has
      // advanced the delta cursor, every backfill chunk returns 0
      // even though the wider window has plenty of older mail to
      // pull, and the walker prematurely declares "caught up".
      const result = await sync(days, { forceFull: true })
      onChunkCompleted?.(result, days)

      // Quotech-backend dispatches the actual sync to Celery and
      // returns 202 with a task_id but NO emails counts. Resolve
      // the real outcome by polling /api/emails/sync/status/ until
      // the EmailSyncLog row reaches a terminal state, then advance
      // the cursor with the REAL ``emails_synced`` count.
      //
      // Why this matters: the previous version treated `queued`
      // chunks as state-machine no-ops, which deadlocked the walker
      // on the smallest window (every chunk → queued → no advance →
      // next chunk = 30d again). Even when the worker successfully
      // ingested mail, total_created stayed at 0 forever and the
      // user saw "+0 added · N chunks" with the count frozen.
      let fetched = result?.emails_fetched ?? 0
      let created = result?.emails_created ?? 0
      let queuedTimedOut = false
      let queuedErrored = false
      let queuedErrorMessage: string | null = null

      if (result?.queued) {
        // Tell the global sync state we're still working on this
        // chunk while the worker processes (otherwise the public
        // `isSyncing` flag drops to false the moment the POST
        // returns and the Sync button visibly strobes between
        // "Sync" and "Syncing" as each chunk fires). Bracket is
        // ref-counted so concurrent waiters compose safely.
        setSyncQueuedWaiting(true)
        try {
          const resolution = await resolveQueuedChunk(firedAt)
          fetched = resolution.fetched
          created = resolution.created
          if (resolution.status === 'error') {
            queuedErrored = true
            queuedErrorMessage = resolution.error_message
          } else if (resolution.status === 'running') {
            persist({
              ...current,
              last_run_at: Date.now(),
              last_outcome: 'progress',
              last_message: `Worker still running at ${days}d. Waiting for it to finish before the next window.`,
            })
            return
          } else if (resolution.status === 'timeout') {
            queuedTimedOut = true
          }
          try {
            // eslint-disable-next-line no-console
            console.debug('[backfill] queued chunk resolved', {
              requestId,
              days_back: days,
              task_id: result.task_id,
              resolution,
            })
          } catch { /* ignore */ }
        } finally {
          setSyncQueuedWaiting(false)
        }
      }

      const reachedHorizon = days >= MAX_DAYS
      const empty = created === 0
      const consecutive_empty = empty ? current.consecutive_empty + 1 : 0
      // Churn fix (BUG recurrence of `found==synced`): complete on the
      // first CLEAN pass at the widest window. ``created`` here is the
      // backend ``emails_synced`` = created + re-updated, so it never
      // hits 0 on a re-pull and the legacy ``consecutive_empty`` check
      // looped the 10-year window forever. See isBackfillComplete.
      const complete = isBackfillComplete({
        reachedHorizon,
        created,
        consecutiveEmpty: consecutive_empty,
        queuedErrored,
        queuedTimedOut,
      })

      // CRITICAL: advance ``oldest_days`` to the window we just
      // successfully covered, EVEN WHEN ``created`` is 0. The earlier
      // version only advanced on non-empty chunks, which deadlocked the
      // walk at the smallest step whenever the recent window was already
      // fully cached locally — exactly the situation where a healthy
      // mailbox sits between background runs. ``consecutive_empty`` is
      // what determines completion at the horizon, not the cursor.
      // Compose a human message that reflects which path resolved.
      // Errors and timeouts get their own copy so the sidebar surfaces
      // why progress feels stuck instead of pretending everything's
      // fine. The cursor still advances on error/timeout — the next
      // chunk widens the window so a single bad step doesn't deadlock
      // the entire walk.
      const queuedFailureMsg = queuedErrored
        ? `Worker error at ${days}d: ${queuedErrorMessage || 'see Recent issues'}.`
        : queuedTimedOut
          ? `Worker still running at ${days}d after 60s - advancing; will reconcile on next pass.`
          : null

      const successMsg = complete
        ? `Backfill complete - walked back ~${Math.round(MAX_DAYS / 365)} years; mailbox exhausted.`
        : empty
          ? reachedHorizon
            ? `No older mail at ${days}d (${consecutive_empty}/${COMPLETE_AFTER_EMPTY} empty); will retry then finish.`
            : `No new mail in the last ${days} days; widening window next.`
          : `Backfilled ${created} email(s) from the last ${days} days (fetched ${fetched}).`

      const next: BackfillState = {
        oldest_days: days,
        consecutive_empty,
        last_run_at: Date.now(),
        last_outcome: queuedErrored
          ? 'error'
          : complete
            ? 'complete'
            : 'progress',
        last_message: queuedFailureMsg ?? successMsg,
        complete,
        total_chunks: current.total_chunks + 1,
        total_created: current.total_created + created,
        schema_version: SCHEMA_VERSION,
      }
      persist(next)

      // Log selectively to avoid the "Recent issues" panel filling up
      // with hundreds of routine empties. Worth recording:
      //  - completion (terminal success — once)
      //  - chunks that actually pulled new mail (proof of progress)
      //  - first empty step at the horizon (signal we're winding down)
      //  - any worker error or timeout (so support can triage)
      // Routine empty steps just print to the browser console; the
      // sidebar progress bar already shows continuous status visually.
      const shouldRecord =
        complete ||
        !empty ||
        queuedErrored ||
        queuedTimedOut ||
        (reachedHorizon && consecutive_empty === 1)
      if (shouldRecord) {
        void reportClientIssue({
          source: 'client.email.sync',
          severity: queuedErrored ? 'error' : queuedTimedOut ? 'warning' : 'info',
          reason: queuedErrored
            ? 'backfill_worker_error'
            : queuedTimedOut
              ? 'backfill_worker_timeout'
              : complete
                ? 'backfill_complete'
                : empty
                  ? 'backfill_empty'
                  : 'backfill_progress',
          message: next.last_message,
          requestId,
          context: {
            days_back: days,
            emails_fetched: fetched,
            emails_created: created,
            oldest_days_after: next.oldest_days,
            consecutive_empty,
            duration_ms: Date.now() - startedAt,
            complete,
          },
        })
      } else {
        // Console-only trace so a developer or support agent inspecting
        // the browser DevTools can still see every chunk's outcome.
        try {
          // eslint-disable-next-line no-console
          console.debug('[backfill] empty step', {
            requestId,
            days_back: days,
            consecutive_empty,
            oldest_days_after: next.oldest_days,
          })
        } catch { /* ignore */ }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Backfill chunk failed'
      const next: BackfillState = {
        ...current,
        last_run_at: Date.now(),
        last_outcome: 'error',
        last_message: `Backfill chunk failed: ${msg}`,
        total_chunks: current.total_chunks + 1,
      }
      persist(next)
      void reportClientIssue({
        source: 'client.email.sync',
        severity: 'warning',
        reason: 'backfill_chunk_failed',
        message: next.last_message,
        requestId,
        context: {
          days_back: days,
          oldest_days: current.oldest_days,
          duration_ms: Date.now() - startedAt,
        },
        detail: err instanceof Error && err.stack ? err.stack : undefined,
      })
    } finally {
      setRunning(false)
    }
  }, [sync, persist, onChunkCompleted])

  // Background loop — fires only while the tab is visible AND the
  // prereqs (Outlook connected, no foreground sync in flight) hold.
  // Cadence is dynamic: faster while progressing, slower in steady state.
  useEffect(() => {
    let timer: number | null = null
    const stepRef = { current: step }
    stepRef.current = step

    const tick = async () => {
      const s = stateRef.current
      if (s.complete) return
      if (!enabledRef.current) return
      if (isSyncingRef.current) return
      if (document.hidden) return

      // Dynamic cooldown (widened 2026-06 to stay under the backend
      // capacity guard — see the BACKFILL_BOOTSTRAP_MS note):
      //   - First-connect (total_chunks < 5):  45s — burst ingest while
      //     user attention is high, so the count visibly rises.
      //   - Productive chunk (`progress`):     3min — steady-state walk.
      //   - Empty/error chunk:                 10min — no point hammering
      //     Graph once we're near the bottom of the mailbox or it's
      //     returning 5xx.
      const since = Date.now() - s.last_run_at
      const isBootstrapping = s.total_chunks < BACKFILL_BOOTSTRAP_CHUNK_LIMIT && !s.complete
      const cooldown = isBootstrapping
        ? BACKFILL_BOOTSTRAP_MS
        : s.last_outcome === 'progress'
          ? BACKFILL_PROGRESS_MS
          : BACKFILL_COOLDOWN_MS
      if (s.last_run_at !== 0 && since < cooldown) return

      await stepRef.current()
    }

    const startTimer = () => {
      if (timer !== null) return
      // Tick every 10s; ``tick`` is guarded by the dynamic cooldown
      // (BOOTSTRAP_MS during first-connect, PROGRESS_MS / COOLDOWN_MS
      // after) so we never queue more than one chunk per interval.
      // 10s is the GCD of all three cadences and keeps the cost
      // bounded at one cheap closure per tick.
      timer = window.setInterval(() => { void tick() }, 10_000)
    }
    const stopTimer = () => {
      if (timer !== null) {
        window.clearInterval(timer)
        timer = null
      }
    }
    let lastHiddenAt = 0
    const onVisibility = () => {
      if (document.hidden) {
        lastHiddenAt = Date.now()
        stopTimer()
      } else {
        void tick()
        startTimer()
      }
    }
    // BUG-0026: gate `focus` on a recent hidden transition. Bare focus
    // events from right-click in Cursor's browser would otherwise re-tick
    // the backfill walker on every contextmenu open.
    const onFocus = () => {
      if (lastHiddenAt > 0 && Date.now() - lastHiddenAt < 30_000) {
        void tick()
        startTimer()
      }
    }
    if (!document.hidden) {
      void tick()
      startTimer()
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)
    return () => {
      stopTimer()
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onFocus)
    }
  }, [step])

  const progressPct = backfillProgressPct(state)

  return { state, progressPct, running, reset, step }
}

/**
 * Read-only view of the backfill state for components that should
 * display progress without driving the loop (e.g. the Sync Settings
 * page). Keeps in sync via the same `storage` events the writer uses,
 * with a low-frequency interval poll as a safety net for same-tab
 * writes (the `storage` event only fires across tabs).
 */
export interface UseBackfillStateViewReturn {
  state: BackfillState
  progressPct: number
  /** Reset the cursor to zero so the loop will re-walk from now. */
  reset: () => void
}

export function useBackfillStateView(
  organisationId: number | null | undefined,
  userId: number | null | undefined,
): UseBackfillStateViewReturn {
  const key = backfillStorageKey(organisationId, userId)
  const [state, setState] = useState<BackfillState>(() => loadState(key))

  useEffect(() => {
    setState(loadState(key))
    const onStorage = (ev: StorageEvent) => {
      if (ev.key !== key) return
      setState(loadState(key))
    }
    window.addEventListener('storage', onStorage)
    // Poll every 5s for same-tab writes (storage event doesn't fire
    // for setters in the same window).
    const id = window.setInterval(() => setState(loadState(key)), 5_000)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.clearInterval(id)
    }
  }, [key])

  const reset = useCallback(() => {
    const next = { ...DEFAULT_STATE, last_message: 'Reset - will re-walk history' }
    saveState(key, next)
    setState(next)
    // Synthesize a storage event for same-tab listeners (the browser
    // doesn't dispatch one for setItem in the active tab).
    try {
      window.dispatchEvent(new StorageEvent('storage', { key, newValue: JSON.stringify(next) }))
    } catch {
      // ignore — event constructor unsupported on very old browsers
    }
  }, [key])

  return { state, progressPct: backfillProgressPct(state), reset }
}
